import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type RegisterRoute from 'frontend-login/routes/register';
import { RESPONSE_CODE } from 'frontend-login/utils/constant-values';
import type { ModelFrom } from 'frontend-login/utils/routes';
import type { LoginResponse } from 'password-form/services/mfa-service';
import nextWithDefault from 'password-form/utils/auth-utils';

export default class RegisterController extends Controller {
  declare model: ModelFrom<RegisterRoute>;

  queryParams = ['next', 'isHandlingRedirect', 'token'];

  @service declare authentication: Services['authentication'];
  @service declare customFetch: Services['custom-fetch'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare sharedAuth: Services['shared-auth'];

  @tracked isHandlingRedirect = false;
  @tracked isNewPasswordValid = false;
  @tracked isRequestRunning = false;
  @tracked next = '';
  @tracked repeatPassword = '';
  token = '';

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get arePasswordsValid() {
    return this.isNewPasswordValid && this.isRepeatPasswordValid;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    if (this.model?.ssoEnforced) {
      return this.sharedAuth.isEmailValid;
    }

    if (!this.sharedAuth.isEmailValid) {
      return false;
    }

    return this.isNewPasswordValid && this.isRepeatPasswordValid;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRepeatPasswordValid() {
    return this.sharedAuth.password === this.repeatPassword;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSubmitButtonDisabled() {
    if (this.model?.ssoEnforced) {
      return false;
    }

    if (this.isRequestRunning) {
      return true;
    }

    return !(this.sharedAuth.password && this.repeatPassword);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pageTitle() {
    return this.intl.t('login.sign_up');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get submitButtonLabel() {
    // If SSO is enforced, the user will have only one login option. Therefore, we only show the next button and redirect the user to their SSO page
    if (this.model?.ssoEnforced) {
      return this.intl.t('login.next');
    }

    return this.intl.t('login.sign_up');
  }

  /**
   * We don't want to show any error if the user needs to be redirected to the MFA page.
   *
   *  @context When the user is MFA enforced we receive from BE the error code 'sso.error.mfa_enforced'.
   * In a normal flow we should show an error if that code is different from 'ok',
   * but because what we want is to redirect the user to the MFA flow we skip this if and allow the user to continue its registration.
   */

  async signUpWithPassword(): Promise<void> {
    this.isRequestRunning = true;
    let result: LoginResponse;
    let params = {};
    if (this.next) {
      params = {
        next: nextWithDefault(this.next),
      };
    }

    this.sharedAuth.trackSentryUser(this.sharedAuth.emailInputValue);

    try {
      result = await this.customFetch.post('/sso/register/', params, {
        email: this.sharedAuth.emailInputValue,
        password: this.sharedAuth.password,
        email_verification_code: this.token,
      });

      if (
        !result.response.ok &&
        result.data.code !== RESPONSE_CODE.MFA_ENFORCED
      ) {
        this.isRequestRunning = false;
        this.sharedAuth.handleErrors(result, true);
        return;
      }

      this.sharedAuth.cachedCredential =
        await this.authentication.signInWithPassword(
          this.sharedAuth.emailInputValue,
          this.sharedAuth.password,
        );

      this.isRequestRunning = false;

      // After the user is authenticated, we need to set the authenticated email to continue the MFA process
      this.sharedAuth.authenticatedEmail =
        this.sharedAuth.cachedCredential.user.email;

      await this.sharedAuth.completeLoginProcess({
        next: nextWithDefault(this.next),
      });
    } catch (error) {
      this.isRequestRunning = false;
      this.sharedAuth.showErrorMessage();
      this.errorTracking.captureException(error);
    }
  }

  @action
  reset(): void {
    this.sharedAuth.reset();
    this.repeatPassword = '';
  }

  @action
  setNewPasswordValidState(isValid: boolean): void {
    this.isNewPasswordValid = isValid;
  }

  @action
  setRepeatPassword(event: Event): void {
    this.repeatPassword = (event.target as HTMLInputElement).value;
  }

  @action async submitForm(): Promise<void> {
    if (!this.isFormValid) {
      return;
    }

    this.sharedAuth.showError = false;
    this.sharedAuth.emailInputHasErrors = false;

    if (this.sharedAuth.password) {
      await this.signUpWithPassword();
    }

    if (this.model?.ssoEnforced && this.sharedAuth.ssoLoginOptions[0]) {
      await this.sharedAuth.ssoLoginOptions[0].onClickAction();
    }
  }

  @action async submitFormWithEnter(event: KeyboardEvent): Promise<void> {
    if (event.key === 'Enter') {
      await this.submitForm();
    }
  }
}
